// Select size step - allows user to choose a digital or print output.
//

import { ArrowLeft, ArrowRight, Check, Download } from "lucide-react";
import { useState } from "react";

import { Button } from "@/ui/button";

import {
  calculatePrintLayout,
  type OutputSize,
  PHOTO_SIZES,
  type PhotoSize,
  PRINT_SIZES,
  type PrintSize,
} from "../utils/dimensions";

interface SelectSizeStepProps {
  initialSize: OutputSize;
  photoSize: PhotoSize;
  onContinue: (size: OutputSize) => void;
  onBack: () => void;
}

// Select size step component.
//
export function SelectSizeStep({
  initialSize,
  photoSize,
  onContinue,
  onBack,
}: SelectSizeStepProps) {
  const [selectedSize, setSelectedSize] = useState<OutputSize>(initialSize);
  const photo = PHOTO_SIZES[photoSize];

  const sizes = Object.entries(PRINT_SIZES) as [
    PrintSize,
    (typeof PRINT_SIZES)[PrintSize],
  ][];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold">Choose Output</h3>
        <p className="text-muted-foreground mt-1">
          Download individual digital photos or choose a paper size.
        </p>
      </div>

      {/* Size options */}
      <div className="grid gap-4 sm:grid-cols-2">
        <button
          aria-pressed={selectedSize === "digital"}
          type="button"
          className={`relative rounded-xl border-2 p-6 text-left transition-all ${
            selectedSize === "digital"
              ? "border-primary bg-accent"
              : "border-border hover:border-primary/50"
          }`}
          onClick={() => setSelectedSize("digital")}
        >
          {selectedSize === "digital" && (
            <div className="bg-primary text-primary-foreground absolute top-3 right-3 flex h-6 w-6 items-center justify-center rounded-full">
              <Check className="h-4 w-4" />
            </div>
          )}

          <div className="flex items-center gap-2 text-2xl font-bold">
            <Download className="h-5 w-5" />
            Digital photo
          </div>
          <div className="text-muted-foreground mt-2 text-sm">
            One photo per PNG file
          </div>
          <div className="text-muted-foreground mt-1 text-xs">
            {photo.pixels.width} × {photo.pixels.height} pixels
          </div>
        </button>

        {sizes.map(([sizeKey, sizeConfig]) => {
          const isSelected = selectedSize === sizeKey;
          const layout = calculatePrintLayout(sizeKey, photoSize);

          return (
            <button
              key={sizeKey}
              type="button"
              className={`relative rounded-xl border-2 p-6 text-left transition-all ${
                isSelected
                  ? "border-primary bg-accent"
                  : "border-border hover:border-primary/50"
              } `}
              onClick={() => setSelectedSize(sizeKey)}
            >
              {/* Selected indicator */}
              {isSelected && (
                <div className="bg-primary text-primary-foreground absolute top-3 right-3 flex h-6 w-6 items-center justify-center rounded-full">
                  <Check className="h-4 w-4" />
                </div>
              )}

              {/* Size label */}
              <div className="text-2xl font-bold">{sizeConfig.label}</div>

              {/* Photo count */}
              <div className="text-muted-foreground mt-2 text-sm">
                {layout.photoCount} passport photo
                {layout.photoCount === 1 ? "" : "s"}
              </div>

              {/* Grid info */}
              <div className="text-muted-foreground mt-1 text-xs">
                {layout.grid.columns} × {layout.grid.rows} grid
              </div>
            </button>
          );
        })}
      </div>

      {/* Info box */}
      <div className="bg-accent rounded-xl border p-4">
        <p className="text-muted-foreground text-sm">
          {selectedSize === "digital" ? (
            <>
              Each download contains one {photo.label} photo. Check the issuing
              authority&apos;s required file format and file size before
              submitting it.
            </>
          ) : (
            <>
              Each passport photo is {photo.label}. Choose based on your
              printer&apos;s paper size and how many copies you need.
            </>
          )}
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={() => onContinue(selectedSize)}>
          Continue
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
